<template>
  <div>
    <v-progress-linear v-if="loading" :indeterminate="true"/>
    <v-container fluid v-if="!loading">
      <v-card>
        <v-card-title>
          Web App Feedback
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <v-menu
                ref="dateFromMenu"
                v-model="dateFromMenu"
                :close-on-content-click="false"
                :return-value.sync="dateFrom"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateFrom"
                    label="From Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateFrom"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dateFromMenu = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.dateFromMenu.save(dateFrom)">OK</v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            
            <v-col cols="12" sm="6" md="4">
              <v-menu
                ref="dateToMenu"
                v-model="dateToMenu"
                :close-on-content-click="false"
                :return-value.sync="dateTo"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateTo"
                    label="To Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dateTo"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dateToMenu = false">Cancel</v-btn>
                  <v-btn text color="primary" @click="$refs.dateToMenu.save(dateTo)">OK</v-btn>
                </v-date-picker>
              </v-menu>
              <DebouncedTextField
                v-model="search"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
              ></DebouncedTextField>

            </v-col>
            
            <v-col cols="12" sm="6" md="4">
              <v-card v-if="filteredFeedback.length > 0" outlined class="feedback-summary-card">
                <v-card-title class="text-subtitle-1">Feedback Summary</v-card-title>
                <v-card-text class="py-1">
                  <p class="mb-1">Total Users: {{ latestFeedbackByUser.length }}</p>
                  <p class="mb-1">Positive: {{ positiveCount }} ({{ positivePercentage }}%)</p>
                  <p class="mb-1">Negative: {{ negativeCount }} ({{ negativePercentage }}%)</p>
                  <p class="mb-1 text-caption">
                    <v-icon x-small color="info" class="mr-1">mdi-information-outline</v-icon>
                    Only counting latest feedback from each user
                  </p>
                  <p v-if="hideEmptyComments && feedback.length > filteredFeedback.length" class="mb-0">
                    <v-chip x-small color="grey" class="mr-1">Note</v-chip>
                    {{ feedback.length - filteredFeedback.length }} entries hidden
                  </p>
                </v-card-text>
                <v-card-actions class="pt-0">
                  <v-spacer></v-spacer>
                  <v-btn small text color="primary" @click="fetchFeedback">
                    <v-icon small left>mdi-refresh</v-icon>
                    Refresh
                  </v-btn>
                </v-card-actions>
              </v-card>
              <v-btn v-else color="primary" @click="fetchFeedback">
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-col>
          </v-row>
          
          <v-row>
            <v-col cols="12">
              <v-switch
                v-model="hideEmptyComments"
                label="Hide feedback with no comments"
                color="primary"
                hide-details
                class="mt-2"
              ></v-switch>
            </v-col>
          </v-row>
          
          <v-tabs v-model="activeTab">
            <v-tab>All Feedback</v-tab>
            <v-tab>Grouped by User</v-tab>
          </v-tabs>
          
          <v-tabs-items v-model="activeTab">
            <v-tab-item>
              <!-- All Feedback Tab -->
              <v-data-table
                :headers="headers"
                :items="filteredFeedback"
                :search="search"
                :loading="loading"
                :items-per-page="15"
                :footer-props="{
                  'items-per-page-options': [15, 30, 50, 100, -1],
                }"
                class="elevation-1"
              >
                <template v-slot:item.rating="{ item }">
                  <v-chip
                    :color="item.rating === 'positive' ? 'success' : 'error'"
                    dark
                  >
                    {{ item.rating }}
                  </v-chip>
                </template>
                
                <template v-slot:item.date="{ item }">
                  {{ formatDate(item.date) }}
                </template>
                
                <template v-slot:item.timestamp="{ item }">
                  {{ formatDateTime(item.timestamp) }}
                </template>
                
                <template v-slot:item.comment="{ item }">
                  <div class="d-flex align-center">
                    {{ item.comment || 'No comment' }}
                    <v-chip
                      x-small
                      v-if="item.duplicateCount > 0"
                      color="info"
                      class="ml-2"
                    >
                      {{ item.duplicateCount + 1 }}x
                    </v-chip>
                  </div>
                </template>
              </v-data-table>
            </v-tab-item>
            
            <v-tab-item>
              <!-- Grouped by User Tab -->
              <div v-if="feedbackByUser && Object.keys(feedbackByUser).length > 0">
                <v-list dense class="feedback-list">
                  <template v-for="(userFeedback, userId) in feedbackByUser">
                    <div :key="userId" class="user-section">
                      <div class="user-id-label px-2 py-1">
                        <small class="text-caption grey--text">{{ userId }}</small>
                      </div>
                      <!-- User feedback items -->
                      <v-list-item
                        v-for="(item, index) in userFeedback"
                        :key="`${userId}-${index}`"
                        class="feedback-list-item"
                      >
                        <v-list-item-content>
                          <div class="d-flex align-center">
                            <div class="mr-2">
                              <v-chip x-small outlined>{{ formatDate(item.date) }}</v-chip>
                            </div>
                            <v-chip
                              x-small
                              :color="item.rating === 'positive' ? 'success' : 'error'"
                              dark
                              class="mr-2"
                            >
                              {{ item.rating }}
                            </v-chip>
                            <div class="comment-text">
                              {{ item.comment || 'No comment' }}
                              <v-chip
                                x-small
                                v-if="item.duplicateCount > 0"
                                color="info"
                                class="ml-1"
                              >
                                {{ item.duplicateCount + 1 }}x
                              </v-chip>
                            </div>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider v-if="Object.keys(feedbackByUser).indexOf(userId) < Object.keys(feedbackByUser).length - 1"></v-divider>
                    </div>
                  </template>
                </v-list>
              </div>
              <v-alert
                v-else
                type="info"
                outlined
              >
                No feedback data available for the selected date range.
              </v-alert>
            </v-tab-item>
          </v-tabs-items>
          
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import api from '@/api';
import moment from 'moment';
import DebouncedTextField from "@/components/DebouncedTextField.vue";

export default {
  name: 'WebAppFeedback',
  components: {DebouncedTextField},
  
  data() {
    return {
      loading: false,
      search: '',
      feedback: [],
      hideEmptyComments: true,
      dateFrom: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      dateTo: moment().format('YYYY-MM-DD'),
      dateFromMenu: false,
      dateToMenu: false,
      activeTab: 0,
      headers: [
        { text: 'Date', value: 'date', sortable: true },
        { text: 'Time', value: 'timestamp', sortable: true },
        { text: 'Rating', value: 'rating', sortable: true },
        { text: 'Comment', value: 'comment', sortable: true },
        { text: 'User ID', value: 'uid', sortable: true },
        { text: 'IP Address', value: 'ip', sortable: true }
      ]
    };
  },
  
  computed: {
    filteredFeedback() {
      let result = this.feedback;
      
      // First filter out empty comments if needed
      if (this.hideEmptyComments) {
        result = result.filter(item => item.comment && item.comment.trim() !== '');
      }
      
      // Then filter out duplicate comments from the same user
      const uniqueComments = new Map();
      
      result = result.reduce((acc, item) => {
        const userId = item.uid || 'anonymous';
        const comment = item.comment || '';
        const key = `${userId}:${comment}`;
        
        if (!uniqueComments.has(key)) {
          // First occurrence of this comment from this user
          uniqueComments.set(key, {
            item,
            count: 1,
            index: acc.length
          });
          acc.push({...item, duplicateCount: 0});
        } else {
          // Duplicate comment, increment count
          const data = uniqueComments.get(key);
          data.count++;
          acc[data.index].duplicateCount = data.count - 1;
        }
        
        return acc;
      }, []);
      
      return result;
    },
    
    // Get only the most recent feedback from each user for summary calculations
    latestFeedbackByUser() {
      // Group feedback by user ID and keep only the most recent entry
      const userLatestFeedback = {};
      
      this.filteredFeedback.forEach(item => {
        const userId = item.uid || 'anonymous';
        const timestamp = new Date(item.timestamp || 0).getTime();
        
        if (!userLatestFeedback[userId] || 
            timestamp > new Date(userLatestFeedback[userId].timestamp || 0).getTime()) {
          userLatestFeedback[userId] = item;
        }
      });
      
      // Convert the object to an array
      return Object.values(userLatestFeedback);
    },
    
    positiveCount() {
      return this.latestFeedbackByUser.filter(item => item.rating === 'positive').length;
    },
    
    negativeCount() {
      return this.latestFeedbackByUser.filter(item => item.rating === 'negative').length;
    },
    
    positivePercentage() {
      if (this.latestFeedbackByUser.length === 0) return 0;
      return Math.round((this.positiveCount / this.latestFeedbackByUser.length) * 100);
    },
    
    negativePercentage() {
      if (this.latestFeedbackByUser.length === 0) return 0;
      return Math.round((this.negativeCount / this.latestFeedbackByUser.length) * 100);
    },
    
    feedbackByUser() {
      // Group feedback by user ID
      const grouped = {};
      
      this.filteredFeedback.forEach(item => {
        const userId = item.uid || 'anonymous';
        if (!grouped[userId]) {
          grouped[userId] = [];
        }
        
        // Check for duplicate comments
        const comment = item.comment || '';
        const existingItemIndex = grouped[userId].findIndex(
          existing => (existing.comment || '') === comment
        );
        
        if (existingItemIndex >= 0) {
          // Increment the count for duplicate comments
          const existingItem = grouped[userId][existingItemIndex];
          if (!existingItem.duplicateCount) {
            existingItem.duplicateCount = 1;
          }
          existingItem.duplicateCount++;
        } else {
          // Add new item
          grouped[userId].push({...item, duplicateCount: 0});
        }
      });
      
      // Sort each user's feedback by date (newest first)
      Object.keys(grouped).forEach(userId => {
        grouped[userId].sort((a, b) => {
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
      });
      
      return grouped;
    }
  },
  
  mounted() {
    this.fetchFeedback();
  },
  
  methods: {
    async fetchFeedback() {
      this.loading = true;
      try {
        const response = await api.get('/feedback/web-app-feedback', {
          params: {
            dateFrom: this.dateFrom,
            dateTo: this.dateTo
          }
        });
        
        this.feedback = response.data;
      } catch (error) {
        console.error('Error fetching feedback:', error);
        this.$store.commit('setError', 'Failed to fetch feedback data');
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      return moment(dateString).format('YYYY-MM-DD');
    },
    
    formatDateTime(dateTimeString) {
      return moment(dateTimeString).format('YYYY-MM-DD HH:mm:ss');
    },
    
    getUserSentiment(userFeedback) {
      // Calculate the sentiment based on the ratio of positive to negative feedback
      const positiveCount = userFeedback.filter(item => item.rating === 'positive').length;
      const negativeCount = userFeedback.filter(item => item.rating === 'negative').length;
      const total = userFeedback.length;
      
      if (total === 0) {
        return { text: 'No feedback', color: 'grey' };
      }
      
      const positivePercentage = Math.round((positiveCount / total) * 100);
      
      if (positiveCount === total) {
        return { text: 'All Positive', color: 'success' };
      } else if (negativeCount === total) {
        return { text: 'All Negative', color: 'error' };
      } else if (positivePercentage >= 75) {
        return { text: 'Mostly Positive', color: 'success' };
      } else if (positivePercentage >= 50) {
        return { text: 'Mixed (Positive)', color: 'light-green' };
      } else if (positivePercentage >= 25) {
        return { text: 'Mixed (Negative)', color: 'orange' };
      } else {
        return { text: 'Mostly Negative', color: 'error' };
      }
    }
  }
};
</script>

<style scoped>
.v-data-table {
  margin-top: 20px;
}

.feedback-list {
}

.comment-text {
  word-break: break-word;
  flex: 1;
}

.feedback-list-item {
  padding: 4px 0;
  min-height: 0 !important;
}

/* Add alternating background colors for better readability */
.feedback-list-item:nth-child(odd) {
  background-color: rgba(0, 0, 0, 0.03);
}

.user-section {
  margin-bottom: 8px;
}

.user-id-label {
  background-color: rgba(0, 0, 0, 0.02);
  border-left: 3px solid rgba(0, 0, 0, 0.1);
}

.feedback-summary-card {
  height: 100%;
  font-size: 0.9rem;
}

.feedback-summary-card .v-card__title {
  padding-bottom: 8px;
}

.feedback-summary-card p {
  line-height: 1.3;
}
</style>